import { useMemo } from 'react';

import { type DEFAULT_AD_FILTER_PARAMS } from '../../../shared/constants';
import { ENTITLEMENT_TAB_LIST_AD_FILTER } from '../../../shared/constants/entitlements';
import {
  useGetApplicationEndpointsMutation,
  useGetEntitlementsMutation,
  useGetRisksMutation,
  useLazyGetEntitlementTypesQuery,
} from '../../ViolationsTab/data-layer/violationsTabApi';

/**
 * Custom hook to generate a query map for advanced filters.
 *
 * This hook retrieves query functions for different advanced filters and
 * returns a memoized object mapping filter keys to their corresponding query functions.
 *
 * @param {boolean} [strict=false] - If `true`, only includes the query function which has initial options;
 *                                   if `false`, includes all types of query functions.
 * @returns {Object} Query map where keys are filter values and values are query functions.
 */
const useGetQueryMap = (strict = false) => {
  const [fetchRiskFilters] = useGetRisksMutation();
  const [fetchEntitlements] = useGetEntitlementsMutation();
  const [fetchEntitlementTypes] = useLazyGetEntitlementTypesQuery();
  const [fetchApplicationName] = useGetApplicationEndpointsMutation();

  const queryMap = useMemo(() => {
    // Wrapper for riskName to handle empty search strings
    const fetchRiskFiltersWrapper = (
      params: typeof DEFAULT_AD_FILTER_PARAMS
    ) => {
      // Return empty array if search is empty or blank
      if (!params.search || params.search.trim() === '') {
        return {
          abort: () => {},
          unwrap: () => Promise.resolve([]),
        };
      }

      return fetchRiskFilters(params);
    };

    const queryFn = {};

    const isEmptyQueryMap = Boolean(Object.keys(queryFn).length === 0);

    if (strict && isEmptyQueryMap) {
      return {
        EMPTY_QUERY_FN_KEY: () => ({
          unwrap() {
            return Promise.resolve('empty_query_fn_run_for_query');
          },
        }),
      };
    }

    if (strict) {
      return queryFn;
    }

    return {
      [ENTITLEMENT_TAB_LIST_AD_FILTER.RISKNAME.value]: fetchRiskFiltersWrapper,
      [ENTITLEMENT_TAB_LIST_AD_FILTER.ENTITLEMENTVALUE.value]:
        fetchEntitlements,
      [ENTITLEMENT_TAB_LIST_AD_FILTER.ENTITLEMENTTYPE.value]:
        fetchEntitlementTypes,
      [ENTITLEMENT_TAB_LIST_AD_FILTER.APPLICATION.value]: fetchApplicationName,
    };
  }, [
    strict,
    fetchRiskFilters,
    fetchEntitlements,
    fetchEntitlementTypes,
    fetchApplicationName,
  ]);

  return queryMap;
};

export default useGetQueryMap;
